import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { APIS_KEYS } from "../../apis-keys";
import { IPropsGetAllOwnerUsers, IResponseGetAllOwnerUsers } from "./types";
import { GetAllOwnerUsersUrl } from "../../../utils/constants";
import type { AxiosInstance, AxiosResponse } from "axios";
import { useApi } from "../../useApi";

async function getAllOwnerUsers(
  {
    pageSize,
    pageNumber,
    name,
    userTypes,
    teamMemberStatus,
    ...rest
  }: IPropsGetAllOwnerUsers,
  apiPrivate: AxiosInstance
) {
  try {
    // Build query parameters manually to handle arrays
    const params = new URLSearchParams();
    params.append("pageSize", pageSize);
    params.append("pageNumber", pageNumber?.toString());

    if (name) {
      params.append("name", name);
    }

    if (userTypes && userTypes?.length > 0) {
      userTypes.forEach((type) => params?.append("userTypes", type));
    }

    if (teamMemberStatus && teamMemberStatus.length > 0) {
      teamMemberStatus?.forEach((status) =>
        params.append("teamMemberStatus", status?.toString())
      );
    }

    // Add any other parameters
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value?.toString());
      }
    });

    const response: AxiosResponse<IResponseGetAllOwnerUsers> =
      await apiPrivate.get(`${GetAllOwnerUsersUrl}?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    throw new Error("something went wrong");
  }
}

export function useGetAllOwnerUsers(
  data: IPropsGetAllOwnerUsers,
  options?:
    | Omit<
        UseQueryOptions<
          IResponseGetAllOwnerUsers,
          unknown,
          IResponseGetAllOwnerUsers
        >,
        "queryKey" | "queryFn"
      >
    | undefined
) {
  const { apiPrivate } = useApi();

  // Create a flat array of all parameter values for the query key
  const queryKeyValues = [
    data.pageSize,
    data.pageNumber,
    data.name,
    ...(data.userTypes || []),
    ...(data.teamMemberStatus || []),
  ];

  return useQuery({
    queryKey: [APIS_KEYS.GETALLOWNERUSERS, ...queryKeyValues],
    queryFn: () => getAllOwnerUsers(data, apiPrivate),
    ...options,
  });
}
